import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
    Portal,
    Box,
    Typography,
    TextField,
    Autocomplete,
    Checkbox,
    FormControlLabel,
    InputAdornment,
    FormControl,
    FormLabel,
    RadioGroup,
    Radio,
    Alert,
    Chip,
    CircularProgress,
    Tooltip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { toast } from 'react-toastify';
import EnumsService from '../../../../Services/enums.service';
import MorningApiService from '../../../../Services/morningApi.service';
import styles from './MorningApiImportModal.module.scss';

const TAB_IMPORT = 'import';
const TAB_SETTINGS = 'settings';

const KINDS = ['incomes', 'payments', 'expenses', 'clients', 'suppliers'];
const JOURNAL_KINDS = ['incomes', 'payments', 'expenses'];
const DEFAULT_SORT_CODES = { incomes: '100', payments: '3', expenses: '' };
const DEFAULT_ACCOUNTS = { incomes: '4', payments: '', expenses: '' };
// Expenses default to the sort code and account card named "הוצאות"; per supplier the backend
// overrides these from the supplier card's configured sort code.
const EXPENSE_DEFAULT_NAME = 'הוצאות';

const toIsoDate = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const defaultRange = () => {
    const now = new Date();
    const firstOfPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastOfPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: toIsoDate(firstOfPrevMonth), to: toIsoDate(lastOfPrevMonth) };
};

const formatAmount = (amount) => {
    if (amount == null) return '';
    return new Intl.NumberFormat('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
};

const formatDateTime = (value) => {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('he-IL');
};

const headCellSx = { fontWeight: 700, fontSize: 12 };
const cellSx = { fontSize: 12 };
const wrapCellSx = { fontSize: 12, maxWidth: 240, whiteSpace: 'normal', wordBreak: 'break-word' };

const MorningApiImportModal = ({
    open,
    onClose,
    caseId,
    token,
    onSuccess,
    sortCodeIdOptions = [],
    accountCardOptions: accountCardOptionsProp = [],
    t,
    direction = 'rtl',
    initialTab = TAB_IMPORT,
}) => {
    const [tab, setTab] = useState(initialTab);

    // Import tab state
    const [kind, setKind] = useState('incomes');
    const [{ from: initialFrom, to: initialTo }] = useState(defaultRange);
    const [fromDate, setFromDate] = useState(initialFrom);
    const [toDate, setToDate] = useState(initialTo);
    const [sortCodeId, setSortCodeId] = useState(DEFAULT_SORT_CODES.incomes);
    const [defaultAccountId, setDefaultAccountId] = useState(DEFAULT_ACCOUNTS.incomes);
    const [paymentTypeToCardId, setPaymentTypeToCardId] = useState({});
    const [downloadDocuments, setDownloadDocuments] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [localAccountCards, setLocalAccountCards] = useState([]);

    // Credentials (settings tab) state
    const [credentials, setCredentials] = useState(null);
    const [credLoading, setCredLoading] = useState(false);
    const [credBusy, setCredBusy] = useState(false);
    const [clientId, setClientId] = useState('');
    const [clientSecret, setClientSecret] = useState('');
    const [environment, setEnvironment] = useState('Live');
    const [showSecret, setShowSecret] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const isRtl = direction === 'rtl';
    const isJournal = JOURNAL_KINDS.includes(kind);

    const applyCredStatus = useCallback((s) => {
        setCredentials(s);
        setClientId(s?.clientId || '');
        setEnvironment(s?.environment || 'Live');
        setClientSecret('');
    }, []);

    const loadCredentials = useCallback(async () => {
        if (!caseId || !token) return;
        setCredLoading(true);
        try {
            const s = await MorningApiService.GetCredentials(caseId, token);
            applyCredStatus(s);
        } catch (err) {
            toast.error(err?.message || t('MorningLoadCredentialsFailed'));
        } finally {
            setCredLoading(false);
        }
    }, [caseId, token, applyCredStatus, t]);

    useEffect(() => {
        if (!open || !caseId || !token) return;
        setTab(initialTab);
        setTestResult(null);
        loadCredentials();
    }, [open, caseId, token, initialTab, loadCredentials]);

    useEffect(() => {
        if (!open || !caseId || !token) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await EnumsService.GetAccountCardsByCaseId(caseId, token);
                if (cancelled || !res?.ok) return;
                const data = await res.json();
                setLocalAccountCards(data || []);
            } catch (e) {
                if (!cancelled) console.error('Fetch account cards failed:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [open, caseId, token]);

    const cardOptions = useMemo(() => {
        const source = localAccountCards.length > 0 ? localAccountCards : accountCardOptionsProp || [];
        return source
            .filter((c) => c.value1 !== 'AddItem')
            .map((c) => {
                const cid = String(c.cardId ?? c.value1 ?? '');
                const name = c.name || '';
                return { cardId: cid, name, label: name ? `${cid} - ${name}` : cid };
            })
            .filter((c) => c.cardId);
    }, [localAccountCards, accountCardOptionsProp]);

    // For expenses: the sort code / account card named "הוצאות" (fallbacks when a supplier has none).
    const expenseDefaults = useMemo(() => {
        const sc = (sortCodeIdOptions || []).find((o) => (o.sortCodeName || '').trim() === EXPENSE_DEFAULT_NAME);
        const card = cardOptions.find((o) => (o.name || '').trim() === EXPENSE_DEFAULT_NAME);
        return { sortCodeId: sc ? String(sc.sortCodeId) : '', debitCardId: card ? card.cardId : '' };
    }, [sortCodeIdOptions, cardOptions]);

    const cardLabel = useCallback(
        (cid) => {
            if (cid == null || cid === '') return '';
            return cardOptions.find((c) => c.cardId === String(cid))?.label ?? String(cid);
        },
        [cardOptions]
    );

    const resetPreview = () => {
        setPreviewData(null);
        setPage(0);
    };

    const handleKindChange = (next) => {
        if (!next || next === kind) return;
        setKind(next);
        if (next === 'expenses') {
            setSortCodeId(expenseDefaults.sortCodeId);
            setDefaultAccountId(expenseDefaults.debitCardId);
        } else {
            setSortCodeId(DEFAULT_SORT_CODES[next] ?? '');
            setDefaultAccountId(DEFAULT_ACCOUNTS[next] ?? '');
        }
        resetPreview();
    };

    // Apply the "הוצאות" defaults once the options have loaded (they may arrive after the tab is shown).
    useEffect(() => {
        if (kind !== 'expenses') return;
        if (!sortCodeId && expenseDefaults.sortCodeId) setSortCodeId(expenseDefaults.sortCodeId);
        if (!defaultAccountId && expenseDefaults.debitCardId) setDefaultAccountId(expenseDefaults.debitCardId);
    }, [kind, expenseDefaults, sortCodeId, defaultAccountId]);

    const buildRequest = () => ({
        caseId,
        fromDate: isJournal ? fromDate : null,
        toDate: isJournal ? toDate : null,
        sortCodeId,
        defaultAccountId,
        paymentTypeToCardId: kind === 'payments' ? paymentTypeToCardId : null,
        classificationToCardId: null,
        downloadDocuments: isJournal && downloadDocuments,
    });

    const dateRangeInvalid = isJournal && (!fromDate || !toDate || fromDate > toDate);
    const hasCredentials = !!credentials?.hasCredentials;

    const handlePreview = async () => {
        if (!hasCredentials || dateRangeInvalid) return;
        try {
            setProcessing(true);
            const res = await MorningApiService.Preview(kind, buildRequest(), token);
            setPreviewData({ ...res, kind });
            setPage(0);
        } catch (err) {
            console.error('Morning API preview failed:', err);
            toast.error(err?.message || t('MorningPreviewFailed'));
        } finally {
            setProcessing(false);
        }
    };

    const missingMappings = useMemo(() => {
        if (!previewData || previewData.kind !== kind) return [];
        if (kind === 'payments') {
            return (previewData.paymentTypes || []).filter((o) => !paymentTypeToCardId[o.key]).map((o) => o.label);
        }
        return [];
    }, [previewData, kind, paymentTypeToCardId]);

    const canImport =
        !!previewData &&
        previewData.kind === kind &&
        !processing &&
        (previewData.validCount ?? 0) > 0 &&
        missingMappings.length === 0;

    const handleImport = async () => {
        if (!canImport) return;
        try {
            setProcessing(true);
            const res = await MorningApiService.Import(kind, buildRequest(), token);
            const processed = res?.processedCount ?? 0;
            const errors = res?.errorCount ?? 0;
            const duplicates = res?.duplicateCount ?? 0;
            const createdCards = res?.createdCardsCount ?? 0;
            let msg = `${t('ImportSuccessful')} – ${processed} ${t('processed')}`;
            if (createdCards > 0) msg += `, ${createdCards} ${t('MorningCardsCreated')}`;
            if (duplicates > 0) msg += `. ${duplicates} ${t('DuplicatesSkipped') || 'duplicates skipped'}`;
            if (errors > 0) {
                toast.warning(`${msg}. ${t('Errors')}: ${errors}. ${(res?.errorMessages || []).slice(0, 3).join(' ')}`);
            } else {
                toast.success(msg);
            }
            onSuccess?.(res);
            handleClose();
        } catch (err) {
            console.error('Morning API import failed:', err);
            toast.error(err?.message || t('ImportFailed'));
        } finally {
            setProcessing(false);
        }
    };

    // ───────── Credentials handlers ─────────

    const canSaveCredentials = !!clientId.trim() && (hasCredentials || !!clientSecret.trim());

    const handleSaveCredentials = async () => {
        if (!canSaveCredentials) return;
        try {
            setCredBusy(true);
            const saved = await MorningApiService.SaveCredentials(
                { caseId, clientId: clientId.trim(), clientSecret: clientSecret.trim(), environment },
                token
            );
            applyCredStatus(saved);
            setTestResult(null);
            toast.success(t('MorningCredentialsSaved'));
        } catch (err) {
            toast.error(err?.message || t('MorningSaveCredentialsFailed'));
        } finally {
            setCredBusy(false);
        }
    };

    const handleTestCredentials = async () => {
        try {
            setCredBusy(true);
            const result = await MorningApiService.TestCredentials(caseId, token);
            setTestResult(result);
            const refreshed = await MorningApiService.GetCredentials(caseId, token);
            applyCredStatus(refreshed);
        } catch (err) {
            setTestResult({ success: false, message: err?.message });
        } finally {
            setCredBusy(false);
        }
    };

    const handleDeleteCredentials = async () => {
        if (!window.confirm(t('MorningDeleteCredentialsConfirm'))) return;
        try {
            setCredBusy(true);
            await MorningApiService.DeleteCredentials(caseId, token);
            applyCredStatus({ hasCredentials: false, environment: 'Live' });
            setTestResult(null);
            resetPreview();
            toast.success(t('MorningCredentialsDeleted'));
        } catch (err) {
            toast.error(err?.message || t('MorningDeleteCredentialsFailed'));
        } finally {
            setCredBusy(false);
        }
    };

    const handleClose = () => {
        resetPreview();
        setRowsPerPage(10);
        setClientSecret('');
        setShowSecret(false);
        setTestResult(null);
        onClose();
    };

    const resolveAccounts = (row) => {
        if (kind === 'payments') {
            return { debit: paymentTypeToCardId[row.paymentType] || row.debitAccount, credit: row.creditAccount };
        }
        // Expenses: the backend already resolved the debit from the supplier card / default.
        return { debit: row.debitAccount, credit: row.creditAccount };
    };

    const entries = previewData?.kind === kind ? previewData.entries || [] : [];
    const paginatedEntries = entries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const sortCodeValue = (sortCodeIdOptions || []).find((o) => String(o.sortCodeId) === String(sortCodeId)) || null;
    const defaultAccountLabel =
        kind === 'incomes' ? t('MorningIncomeAccount') : kind === 'expenses' ? t('MorningDefaultExpenseAccount') : null;
    const isWide = tab === TAB_IMPORT && !!previewData && previewData.kind === kind;

    const renderCardPicker = (value, onChange, label) => (
        <Autocomplete
            options={cardOptions}
            getOptionLabel={(o) => o.label || ''}
            isOptionEqualToValue={(o, v) => o.cardId === v.cardId}
            value={cardOptions.find((o) => o.cardId === String(value ?? '')) || null}
            onChange={(_, next) => onChange(next ? next.cardId : '')}
            size="small"
            fullWidth
            renderInput={(params) => <TextField {...params} label={label} variant="standard" />}
        />
    );

    const renderStatus = (row) => {
        if (!row.isValid) {
            return (
                <Typography variant="body2" color="error.main" title={row.errorMessage}>
                    {row.errorMessage}
                </Typography>
            );
        }
        if (row.isDuplicate) {
            return (
                <Typography variant="body2" color="text.secondary">
                    {isJournal ? t('MorningAlreadyImported') : t('MorningCardExists')}
                </Typography>
            );
        }
        return <Typography variant="body2" color="success.main">{t('Valid') || 'OK'}</Typography>;
    };

    const renderCounterparty = (row) => (
        <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
            <span>{row.counterpartyName || '—'}</span>
            {row.willCreateCard ? (
                <Chip size="small" color="info" variant="outlined" label={t('MorningNewCard')} />
            ) : (
                row.resolvedCardId && <Chip size="small" variant="outlined" label={row.resolvedCardId} />
            )}
        </Box>
    );

    const renderImportTab = () => (
        <>
            {credentials && !hasCredentials && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                        <button type="button" className={styles.secondaryBtn} onClick={() => setTab(TAB_SETTINGS)}>
                            {t('MorningSetCredentials')}
                        </button>
                    }
                >
                    {t('MorningNoCredentials')}
                </Alert>
            )}
            {hasCredentials && credentials?.lastTestOk === false && (
                <Alert severity="error" sx={{ mb: 2, wordBreak: 'break-word' }}>
                    {t('MorningConnectionFailed')}
                    {credentials.lastTestMessage ? `: ${credentials.lastTestMessage}` : ''}
                </Alert>
            )}

            <div className={styles.segmented}>
                {KINDS.map((k) => (
                    <button
                        key={k}
                        type="button"
                        className={`${styles.segment}${kind === k ? ` ${styles.segmentActive}` : ''}`}
                        onClick={() => handleKindChange(k)}
                    >
                        {t(`MorningKind_${k}`)}
                    </button>
                ))}
            </div>

            {isJournal && (
                <div className={styles.controlsGrid}>
                    <div className={styles.fieldSm}>
                        <span className={styles.fieldLabel}>{t('FromDate') || 'From'}</span>
                        <TextField
                            type="date"
                            value={fromDate}
                            onChange={(e) => { setFromDate(e.target.value); resetPreview(); }}
                            size="small"
                            variant="standard"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </div>
                    <div className={styles.fieldSm}>
                        <span className={styles.fieldLabel}>{t('ToDate') || 'To'}</span>
                        <TextField
                            type="date"
                            value={toDate}
                            onChange={(e) => { setToDate(e.target.value); resetPreview(); }}
                            size="small"
                            variant="standard"
                            fullWidth
                            error={dateRangeInvalid}
                            InputLabelProps={{ shrink: true }}
                        />
                    </div>
                    <div className={styles.field}>
                        <span className={styles.fieldLabel}>
                            {kind === 'expenses' ? t('MorningDefaultSortCodeExpense') : (t('DefaultSortCode') || 'Default sort code')}
                        </span>
                        {sortCodeIdOptions?.length > 0 ? (
                            <Autocomplete
                                options={sortCodeIdOptions}
                                getOptionLabel={(o) => `${o.sortCodeId} - ${o.sortCodeName ?? ''}`}
                                value={sortCodeValue}
                                onChange={(_, next) => setSortCodeId(next ? String(next.sortCodeId) : '')}
                                size="small"
                                fullWidth
                                renderInput={(params) => (
                                    <TextField {...params} variant="standard" />
                                )}
                            />
                        ) : (
                            <TextField
                                value={sortCodeId}
                                onChange={(e) => setSortCodeId(e.target.value)}
                                size="small"
                                variant="standard"
                                fullWidth
                            />
                        )}
                        {kind === 'expenses' && <p className={styles.fieldHint}>{t('MorningExpenseSupplierOverrideHint')}</p>}
                    </div>
                    {defaultAccountLabel && (
                        <div className={styles.field}>
                            <span className={styles.fieldLabel}>{defaultAccountLabel}</span>
                            {renderCardPicker(defaultAccountId, setDefaultAccountId, '')}
                        </div>
                    )}
                </div>
            )}

            {isJournal && (
                <div className={styles.checkboxRow}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={downloadDocuments}
                                onChange={(e) => setDownloadDocuments(e.target.checked)}
                            />
                        }
                        label={t('MorningDownloadDocuments')}
                    />
                </div>
            )}

            {kind === 'payments' && previewData?.kind === 'payments' && (previewData.paymentTypes || []).length > 0 && (
                <div className={styles.mappingBox}>
                    <p className={styles.sectionTitle} style={{ marginBottom: 8 }}>{t('MorningPaymentTypeMapping')}</p>
                    {previewData.paymentTypes.map((o) => (
                        <div key={o.key} className={styles.mappingRow}>
                            <span className={styles.mappingLabel}>{o.label} ({o.count})</span>
                            <div className={styles.fieldSm}>
                                {renderCardPicker(
                                    paymentTypeToCardId[o.key],
                                    (cid) => setPaymentTypeToCardId((m) => ({ ...m, [o.key]: cid })),
                                    t('DebitAccount') || 'Debit account'
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {missingMappings.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {t('MorningMissingMappings')}: {missingMappings.join(', ')}
                </Alert>
            )}

            {previewData && previewData.kind === kind && (
                <>
                    <div className={styles.summary}>
                        <span>{t('TotalLines') || 'Total'}: <strong>{previewData.totalCount}</strong></span>
                        <span>{t('ValidCount') || 'Valid'}: <strong>{previewData.validCount}</strong></span>
                        <span>{t('MorningDuplicates')}: <strong>{previewData.duplicateCount}</strong></span>
                        <span>{t('ErrorCount') || 'Errors'}: <strong>{previewData.errorCount}</strong></span>
                        <span>{t('MorningNewCards')}: <strong>{previewData.newCardsCount}</strong></span>
                        {isJournal && <span>{t('SumAmount') || 'Sum'}: <strong>{formatAmount(previewData.sumAmount)}</strong></span>}
                    </div>

                    {previewData.errorMessages?.length > 0 && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                {t('Errors') || 'Errors'} ({previewData.errorMessages.length})
                            </Typography>
                            <Box component="ul" className={styles.errorList}>
                                {previewData.errorMessages.map((msg, i) => (
                                    <li key={i}>{msg}</li>
                                ))}
                            </Box>
                        </Alert>
                    )}

                    {entries.length === 0 ? (
                        <div className={styles.emptyState}>{t('MorningNoData')}</div>
                    ) : (
                        <div className={styles.tableWrap}>
                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 460, overflow: 'auto' }}>
                                <Table size="small" stickyHeader dir={direction}>
                                    <TableHead>
                                        {isJournal ? (
                                            <TableRow>
                                                <TableCell sx={headCellSx}>{t('DocumentDate') || 'Date'}</TableCell>
                                                <TableCell sx={headCellSx}>{t('Reference') || 'Reference'}</TableCell>
                                                <TableCell sx={headCellSx}>{t('MorningDocumentType')}</TableCell>
                                                <TableCell sx={headCellSx}>{kind === 'expenses' ? t('Supplier') || 'Supplier' : t('Client') || 'Client'}</TableCell>
                                                <TableCell sx={headCellSx}>{t('Description')}</TableCell>
                                                <TableCell align="right" sx={headCellSx}>{t('Amount')}</TableCell>
                                                <TableCell sx={headCellSx}>VAT%</TableCell>
                                                <TableCell sx={headCellSx}>{t('DebitAccount') || 'Debit'}</TableCell>
                                                <TableCell sx={headCellSx}>{t('CreditAccount') || 'Credit'}</TableCell>
                                                <TableCell sx={headCellSx}>{t('Status') || 'Status'}</TableCell>
                                            </TableRow>
                                        ) : (
                                            <TableRow>
                                                <TableCell sx={headCellSx}>{t('Name') || 'Name'}</TableCell>
                                                <TableCell sx={headCellSx}>{t('MorningTaxId')}</TableCell>
                                                <TableCell sx={headCellSx}>{t('MorningId')}</TableCell>
                                                <TableCell sx={headCellSx}>{t('MorningAccountCard')}</TableCell>
                                                <TableCell sx={headCellSx}>{t('Status') || 'Status'}</TableCell>
                                            </TableRow>
                                        )}
                                    </TableHead>
                                    <TableBody>
                                        {paginatedEntries.map((row, i) => {
                                            const rowSx = {
                                                bgcolor: !row.isValid ? 'action.hover' : 'transparent',
                                                opacity: row.isDuplicate ? 0.6 : 1,
                                            };
                                            if (!isJournal) {
                                                return (
                                                    <TableRow key={`${row.morningId}-${i}`} sx={rowSx}>
                                                        <TableCell sx={wrapCellSx}>{row.counterpartyName || '—'}</TableCell>
                                                        <TableCell sx={cellSx}>{row.counterpartyTaxId || '—'}</TableCell>
                                                        <TableCell sx={cellSx}>{row.morningId}</TableCell>
                                                        <TableCell sx={cellSx}>
                                                            {row.willCreateCard ? (
                                                                <Chip size="small" color="info" variant="outlined" label={t('MorningNewCard')} />
                                                            ) : (
                                                                cardLabel(row.resolvedCardId)
                                                            )}
                                                        </TableCell>
                                                        <TableCell sx={wrapCellSx}>{renderStatus(row)}</TableCell>
                                                    </TableRow>
                                                );
                                            }
                                            const { debit, credit } = resolveAccounts(row);
                                            const amountText = row.currency && row.currency !== 'ILS'
                                                ? `${formatAmount(row.amount)} (${row.currency})`
                                                : formatAmount(row.amount);
                                            return (
                                                <TableRow key={`${row.morningId}-${i}`} sx={rowSx}>
                                                    <TableCell sx={cellSx}>{row.date}</TableCell>
                                                    <TableCell sx={cellSx}>
                                                        {row.documentUrl ? (
                                                            <a href={row.documentUrl} target="_blank" rel="noopener noreferrer">
                                                                {row.number || row.morningId}
                                                            </a>
                                                        ) : (
                                                            row.number || row.morningId
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={cellSx}>
                                                        {kind === 'payments' ? row.paymentTypeName : kind === 'expenses' ? row.classificationName || '—' : row.documentTypeName}
                                                    </TableCell>
                                                    <TableCell sx={wrapCellSx}>{renderCounterparty(row)}</TableCell>
                                                    <TableCell sx={wrapCellSx} title={row.description}>{row.description}</TableCell>
                                                    <TableCell align="right" sx={cellSx}>{amountText}</TableCell>
                                                    <TableCell sx={cellSx}>{row.vatPercent}</TableCell>
                                                    <TableCell sx={cellSx}>{debit ? cardLabel(debit) : '—'}</TableCell>
                                                    <TableCell sx={cellSx}>{credit ? cardLabel(credit) : '—'}</TableCell>
                                                    <TableCell sx={wrapCellSx}>{renderStatus(row)}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                                <TablePagination
                                    component="div"
                                    count={entries.length}
                                    page={page}
                                    onPageChange={(_, p) => setPage(p)}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={(e) => {
                                        setRowsPerPage(parseInt(e.target.value, 10));
                                        setPage(0);
                                    }}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    labelRowsPerPage={t('RowsPerPage') || 'Rows per page'}
                                />
                            </TableContainer>
                        </div>
                    )}
                </>
            )}
        </>
    );

    const renderSettingsTab = () => {
        if (credLoading) {
            return <div className={styles.loading}><CircularProgress className={styles.spinner} /></div>;
        }
        return (
            <div className={styles.credForm}>
                <p className={styles.muted}>{t('MorningCredentialsHelp')}</p>

                <div className={styles.field} style={{ width: '100%' }}>
                    <span className={styles.fieldLabel}>
                        {t('MorningApiKey')}<span className={styles.requiredMark}> *</span>
                    </span>
                    <TextField
                        value={clientId}
                        onChange={(e) => setClientId(e.target.value)}
                        size="small"
                        variant="standard"
                        fullWidth
                        autoComplete="off"
                        inputProps={{ dir: 'ltr' }}
                    />
                </div>

                <div className={styles.field} style={{ width: '100%' }}>
                    <span className={styles.fieldLabel}>
                        {t('MorningApiSecret')}
                        {!credentials?.hasSecret && <span className={styles.requiredMark}> *</span>}
                    </span>
                    <TextField
                        value={clientSecret}
                        onChange={(e) => setClientSecret(e.target.value)}
                        type={showSecret ? 'text' : 'password'}
                        size="small"
                        variant="standard"
                        fullWidth
                        autoComplete="new-password"
                        placeholder={credentials?.hasSecret ? t('MorningSecretSavedPlaceholder') : ''}
                        inputProps={{ dir: 'ltr' }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setShowSecret((v) => !v)}>
                                        {showSecret ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {credentials?.hasSecret && <p className={styles.fieldHint}>{t('MorningSecretSavedHelp')}</p>}
                </div>

                <FormControl>
                    <FormLabel sx={{ fontSize: 13 }}>{t('MorningEnvironment')}</FormLabel>
                    <RadioGroup row value={environment} onChange={(e) => setEnvironment(e.target.value)}>
                        <FormControlLabel value="Live" control={<Radio size="small" />} label={t('MorningEnvironmentLive')} />
                        <FormControlLabel value="Sandbox" control={<Radio size="small" />} label={t('MorningEnvironmentSandbox')} />
                    </RadioGroup>
                </FormControl>

                {hasCredentials && (
                    <p className={styles.statusLine}>
                        {credentials?.lastTestedAt
                            ? `${t('MorningLastTested')}: ${formatDateTime(credentials.lastTestedAt)} – ${credentials.lastTestOk ? t('MorningConnectionOk') : t('MorningConnectionFailed')}`
                            : t('MorningNotTestedYet')}
                    </p>
                )}

                {testResult && (
                    <Alert severity={testResult.success ? 'success' : 'error'} sx={{ wordBreak: 'break-word' }}>
                        {testResult.success
                            ? t('MorningConnectionOk')
                            : `${t('MorningConnectionFailed')}${testResult.message ? `: ${testResult.message}` : ''}`}
                    </Alert>
                )}
            </div>
        );
    };

    if (!open) return null;

    return (
        <Portal>
            <div
                className={`${styles.overlay} ${isRtl ? styles.rtl : styles.ltr}`}
                onClick={handleClose}
            >
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={`${styles.modal}${isWide ? ` ${styles.modalWide}` : ''}`}>
                        <div className={styles.body}>
                            <div className={styles.header}>
                                <Tooltip title={t('Close') || 'Close'}>
                                    <IconButton onClick={handleClose} className={styles.closeIcon}>
                                        <CancelIcon className={styles.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div>{t('MorningApiImport')}</div>
                                <div />
                            </div>

                            <div className={styles.tabs}>
                                <button
                                    type="button"
                                    className={`${styles.tab}${tab === TAB_IMPORT ? ` ${styles.tabActive}` : ''}`}
                                    onClick={() => setTab(TAB_IMPORT)}
                                >
                                    {t('MorningKind_import') || t('Import')}
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.tab}${tab === TAB_SETTINGS ? ` ${styles.tabActive}` : ''}`}
                                    onClick={() => setTab(TAB_SETTINGS)}
                                >
                                    {t('MorningApiCredentials')}
                                </button>
                            </div>

                            <div className={styles.scroll}>
                                {tab === TAB_IMPORT ? renderImportTab() : renderSettingsTab()}
                            </div>

                            <div className={styles.footer}>
                                {tab === TAB_SETTINGS && hasCredentials && (
                                    <button
                                        type="button"
                                        className={`${styles.dangerBtn} ${styles.footerSpacer}`}
                                        disabled={credBusy}
                                        onClick={handleDeleteCredentials}
                                    >
                                        {t('Delete')}
                                    </button>
                                )}
                                <button type="button" className={styles.secondaryBtn} onClick={handleClose}>
                                    {t('cancelButton') || t('Close') || 'Cancel'}
                                </button>

                                {tab === TAB_IMPORT ? (
                                    <>
                                        <button
                                            type="button"
                                            className={styles.ghostBtn}
                                            disabled={!hasCredentials || dateRangeInvalid || processing}
                                            onClick={handlePreview}
                                        >
                                            {processing && !previewData
                                                ? <CircularProgress className={styles.spinner} size={18} />
                                                : t('Preview')}
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.primaryBtn}
                                            disabled={!canImport}
                                            onClick={handleImport}
                                        >
                                            {processing && previewData
                                                ? <CircularProgress className={styles.spinner} size={18} color="inherit" />
                                                : t('Import')}
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            className={styles.ghostBtn}
                                            disabled={!hasCredentials || credBusy}
                                            onClick={handleTestCredentials}
                                        >
                                            {t('MorningTestConnection')}
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.primaryBtn}
                                            disabled={!canSaveCredentials || credBusy}
                                            onClick={handleSaveCredentials}
                                        >
                                            {credBusy
                                                ? <CircularProgress className={styles.spinner} size={18} color="inherit" />
                                                : (t('save') || t('Save'))}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default MorningApiImportModal;
